import { ref } from 'vue';

// Global reactive state for the current user
export const currentUser = ref(null);

// Global reactive state for the current page title
export const pageTitle = ref('');

// Normalize API/user payload keys to the PascalCase shape used across views
function normalizeUser(u) {
  if (!u) return null;
  const v = { ...u };
  // Prefer existing PascalCase; otherwise lift from camel/lower-case
  v.Id = v.Id ?? v.id ?? v.userId ?? v.UserId ?? null;
  v.Username = v.Username ?? v.username ?? v.UserName ?? null;
  v.DisplayName = v.DisplayName ?? v.displayName ?? v.name ?? null;
  v.Email = v.Email ?? v.email ?? null;
  v.Mobile = v.Mobile ?? v.mobile ?? null;
  v.Department = v.Department ?? v.department ?? null;
  v.Timezone = v.Timezone ?? v.timezone ?? null;
  // Concurrency/row version commonly varies
  v.RowVersionHex = v.RowVersionHex ?? v.rowVersion ?? v.rowVersionHex ?? null;
  return v;
}

/**
 * Initializes the currentUser state from sessionStorage.
 * Call this once when the app loads.
 */
export function initializeUser() {
  const storedUser = sessionStorage.getItem('currentUser');
  if (storedUser) {
    try {
      const parsed = JSON.parse(storedUser);
      currentUser.value = normalizeUser(parsed);
    } catch (e) {
      sessionStorage.removeItem('currentUser');
      currentUser.value = null;
    }
  } else {
    currentUser.value = null;
  }
}

/**
 * Updates the currentUser state and syncs it with sessionStorage.
 */
export function updateUser(user) {
  const norm = normalizeUser(user);
  currentUser.value = norm;
  if (norm) {
    sessionStorage.setItem('currentUser', JSON.stringify(norm));
  } else {
    sessionStorage.removeItem('currentUser');
  }
}

/**
 * Updates the page title displayed in the header
 */
export function updatePageTitle(title) {
  pageTitle.value = title || '';
}

/**
 * 해외영업본부 관리자 여부 (해외영업본부 > 설정에서 지정)
 *  시스템 관리자와 같은 권한으로 해외영업본부 안의 실적·면장·입금·담당자를 수정·삭제할 수 있다.
 *  화면마다 따로 묻지 않도록 한 번만 조회해 공유한다. 로그인 사용자가 바뀌면 다시 조회한다.
 */
export const isOverseasAdmin = ref(false);
export const overseasCaps = ref([]);   // 이 사람이 실제로 쓸 수 있는 기능 키 (설정에서 켠 것만)
let osRoleFor = null;                  // 어느 사용자 기준으로 채운 값인지 (사용자가 바뀌면 다시 조회)

/** 시스템(전체) 관리자 여부 */
export function amSystemAdmin() {
  return currentUser.value?.isAdmin === true || currentUser.value?.isAdmin === '1';
}
/**
 * 해외영업본부 기능을 쓸 수 있는지.
 *  시스템 관리자는 항상 가능, 해외영업 관리자는 설정에서 그 기능을 켜 뒀을 때만 가능하다.
 *  기능 키는 backend/src/services/overseasSalesAdmin.js 의 OS_CAPABILITIES 와 같다.
 */
export function osCan(cap) {
  if (amSystemAdmin()) return true;
  return isOverseasAdmin.value && overseasCaps.value.includes(cap);
}

export async function loadOverseasAdmin(force = false) {
  const who = currentUser.value?.Username || null;
  if (!who) { isOverseasAdmin.value = false; overseasCaps.value = []; osRoleFor = null; return false; }
  if (!force && osRoleFor === who) return isOverseasAdmin.value;
  try {
    const token = localStorage.getItem('token');
    const r = await fetch('/api/sales-teams/my-role', {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    const j = await r.json().catch(() => null);
    const ok = r.ok && j?.ok;
    isOverseasAdmin.value = !!(ok && j.data?.isOverseasAdmin);
    overseasCaps.value = ok ? (j.data?.capabilities || []) : [];
    osRoleFor = who;
  } catch {                                   // 실패하면 권한 없음으로 둔다(다음에 다시 시도)
    isOverseasAdmin.value = false; overseasCaps.value = [];
  }
  return isOverseasAdmin.value;
}
